/*
Risk KRIs Service

This service handles all API calls for Key Risk Indicators (KRIs).
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

const apiService = apiClient;

export interface RiskKRI {
  id: number;
  entity_id: number;
  kri_name: string;
  code?: string;
  description?: string;
  is_active: number;
  is_approved: number;
  workflow_state?: string;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
}

export interface KRIQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class RiskKRIsService {
  async getAll(params?: KRIQueryParams): Promise<RiskKRI[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.page) {
        queryParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/rsk/kris${queryString ? `?${queryString}` : ''}`;

      const response: AxiosResponse<ApiResponse<RiskKRI[]>> = await apiService.get(url);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch KRIs');
      }
    } catch (error: any) {
      console.error('Error fetching KRIs:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async getById(kriId: number): Promise<RiskKRI> {
    try {
      const response: AxiosResponse<ApiResponse<RiskKRI>> =
        await apiService.get(`/api/v1/rsk/kris/${kriId}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch KRI');
      }
    } catch (error: any) {
      console.error(`Error fetching KRI ${kriId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async create(kriData: Partial<RiskKRI>, action: 'draft' | 'submit' | 'approve' = 'draft'): Promise<RiskKRI> {
    try {
      const response: AxiosResponse<ApiResponse<RiskKRI>> =
        await apiService.post(`/api/v1/rsk/kris?action=${action}`, kriData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create KRI');
      }
    } catch (error: any) {
      console.error('Error creating KRI:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async update(kriId: number, updateData: Partial<RiskKRI>): Promise<RiskKRI> {
    try {
      const response: AxiosResponse<ApiResponse<RiskKRI>> =
        await apiService.put(`/api/v1/rsk/kris/${kriId}`, updateData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update KRI');
      }
    } catch (error: any) {
      console.error(`Error updating KRI ${kriId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async delete(kriId: number): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> =
        await apiService.delete(`/api/v1/rsk/kris/${kriId}`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete KRI');
      }
    } catch (error: any) {
      console.error(`Error deleting KRI ${kriId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async toggleStatus(kriId: number): Promise<RiskKRI> {
    try {
      const response: AxiosResponse<ApiResponse<RiskKRI>> =
        await apiService.patch(`/api/v1/rsk/kris/${kriId}/toggle`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to toggle KRI status');
      }
    } catch (error: any) {
      console.error(`Error toggling KRI ${kriId} status:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }
}

const riskKRIsService = new RiskKRIsService();
export default riskKRIsService;
